import React, { useEffect, useState } from "react";
import { Save, RotateCcw, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fetchSiteContent, saveSiteContent, resetSiteContent } from "../api";
import { PageHeader } from "./Dashboard";
import ImageUploader from "../components/ImageUploader";
import VideoUploader from "../components/VideoUploader";
import RefreshButton from "../components/RefreshButton";

export default function Sections() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    fetchSiteContent()
      .then(setContent)
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  if (!content) {
    return (
      <div>
        <PageHeader eyebrow="Site" title="Sections" />
        <p className="p-10 text-[#8c8a7e]">Loading…</p>
      </div>
    );
  }

  const set = (path, value) => {
    setContent((c) => {
      const next = JSON.parse(JSON.stringify(c));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveSiteContent(content);
      toast.success("Homepage updated — your changes are now live.");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm("Restore default homepage copy? Your current edits will be replaced.")) return;
    try {
      const fresh = await resetSiteContent();
      setContent(fresh);
      toast.success("Reset to defaults");
    } catch {
      toast.error("Reset failed");
    }
  };

  return (
    <div data-testid="admin-sections">
      <PageHeader eyebrow="Live homepage" title="Sections" sub="Edit any text or image used on the public homepage. Changes go live the moment you save." />
      <div className="p-10 max-w-3xl space-y-10">
        <div className="flex items-center justify-end -mb-4">
          <RefreshButton onClick={reload} loading={loading} testid="admin-sections-refresh" />
        </div>

        {/* Hero */}
        <Card title="Hero" subtitle="Top of the homepage">
          <Field label="Pre-header"><input value={content.hero.pre_header} onChange={(e) => set("hero.pre_header", e.target.value)} className={input} data-testid="sections-hero-pre" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Headline – line 1"><input value={content.hero.headline_part1} onChange={(e) => set("hero.headline_part1", e.target.value)} className={input} /></Field>
            <Field label="Headline – line 2 (italic)"><input value={content.hero.headline_part2} onChange={(e) => set("hero.headline_part2", e.target.value)} className={input} /></Field>
          </div>
          <Field label="Subheadline"><textarea rows={3} value={content.hero.subheadline} onChange={(e) => set("hero.subheadline", e.target.value)} className={`${input} resize-none`} data-testid="sections-hero-sub" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Primary CTA label"><input value={content.hero.cta_primary_label} onChange={(e) => set("hero.cta_primary_label", e.target.value)} className={input} /></Field>
            <Field label="Primary CTA href"><input value={content.hero.cta_primary_href} onChange={(e) => set("hero.cta_primary_href", e.target.value)} className={input} /></Field>
            <Field label="Secondary CTA label"><input value={content.hero.cta_secondary_label} onChange={(e) => set("hero.cta_secondary_label", e.target.value)} className={input} /></Field>
            <Field label="Secondary CTA href"><input value={content.hero.cta_secondary_href} onChange={(e) => set("hero.cta_secondary_href", e.target.value)} className={input} /></Field>
          </div>
          <Field label="Banner image">
            <ImageUploader
              value={content.hero.banner_image_url ? [content.hero.banner_image_url] : []}
              onChange={(urls) => set("hero.banner_image_url", urls[0] || "")}
              folder="asvasa/hero"
              max={1}
              testid="sections-hero-image"
              guidelines="1920 × 1080 px (16:9 landscape) · JPG/PNG/WEBP · under 4 MB · keep the focal subject right of centre"
            />
          </Field>
        </Card>

        {/* Feature video */}
        <Card title="Feature video" subtitle="Optional motion reel below the hero">
          <div className="px-4 py-3 bg-[#1A1A1A]/60 border-l-2 border-[#c3ba9e]/60 text-[12px] text-[#9a9787] leading-relaxed">
            <span className="text-[#c3ba9e]">Instructions:</span> upload a short reel of the atelier or a piece in motion. Recommended <span className="text-[#c3ba9e]">1920 × 1080 (16:9) or 1080 × 1350 (4:5)</span>, MP4 / MOV, under 80 MB, 10–30 seconds, no logo overlays. Autoplay is muted by default — viewers can un-mute via the player controls.
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!content.feature_video?.enabled}
              onChange={(e) => set("feature_video.enabled", e.target.checked)}
              className="accent-[#c3ba9e]"
              data-testid="sections-feature-video-toggle"
            />
            <span className="text-[12px] text-[#9a9787] tracking-wider">Show this section on the homepage</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Eyebrow"><input value={content.feature_video?.eyebrow || ""} onChange={(e) => set("feature_video.eyebrow", e.target.value)} className={input} /></Field>
            <Field label="Heading"><input value={content.feature_video?.heading || ""} onChange={(e) => set("feature_video.heading", e.target.value)} className={input} /></Field>
          </div>
          <Field label="Body copy"><textarea rows={2} value={content.feature_video?.body || ""} onChange={(e) => set("feature_video.body", e.target.value)} className={`${input} resize-none`} /></Field>
          <Field label="Video file">
            <VideoUploader
              value={content.feature_video?.url || ""}
              onChange={(url) => set("feature_video.url", url)}
              folder="asvasa/videos"
              single
              maxSizeMB={80}
              testid="sections-feature-video"
            />
          </Field>
          <Field label="Poster image (shown before play)">
            <ImageUploader
              value={content.feature_video?.poster ? [content.feature_video.poster] : []}
              onChange={(urls) => set("feature_video.poster", urls[0] || "")}
              folder="asvasa/hero"
              max={1}
              testid="sections-feature-video-poster"
              guidelines="Same aspect ratio as the video · 1920 × 1080 (16:9) · JPG/PNG · under 3 MB"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3 text-[12px] text-[#9a9787]">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!content.feature_video?.autoplay} onChange={(e) => set("feature_video.autoplay", e.target.checked)} className="accent-[#c3ba9e]" /> Autoplay</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!content.feature_video?.loop} onChange={(e) => set("feature_video.loop", e.target.checked)} className="accent-[#c3ba9e]" /> Loop</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!content.feature_video?.muted} onChange={(e) => set("feature_video.muted", e.target.checked)} className="accent-[#c3ba9e]" /> Muted</label>
          </div>
        </Card>

        {/* Announcement bar */}
        <Card title="Announcement bar" subtitle="The thin strip above the navbar">
          <Field label="Text"><input value={content.announcement_bar} onChange={(e) => set("announcement_bar", e.target.value)} className={input} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Schedule — visible from (optional)"><input type="datetime-local" value={content.announcement_visible_from || ""} onChange={(e) => set("announcement_visible_from", e.target.value)} className={input} data-testid="sections-ann-from" /></Field>
            <Field label="Schedule — visible until (optional)"><input type="datetime-local" value={content.announcement_visible_until || ""} onChange={(e) => set("announcement_visible_until", e.target.value)} className={input} data-testid="sections-ann-until" /></Field>
          </div>
          <p className="text-[11px] text-[#8c8a7e]">Leave both blank to keep the bar always-on. Set just one to start or end at a fixed time.</p>
        </Card>

        <Card title="Shop page" subtitle="Subheading under the Shop title">
          <Field label="Shop subheading"><textarea rows={2} value={content.shop_subheading || ""} onChange={(e) => set("shop_subheading", e.target.value)} className={`${input} resize-none`} data-testid="sections-shop-sub" /></Field>
        </Card>

        <Card title="Legal & Help pages" subtitle="Footer pages — Shipping, Returns, Size Guide, Garment Care">
          {(["shipping", "returns", "size_guide", "garment_care"]).map((k) => (
            <details key={k} className="border border-[#2a2a2d] p-4" data-testid={`sections-legal-${k}`}>
              <summary className="cursor-pointer text-[#e9e6db] capitalize">{k.replace("_", " ")}</summary>
              <div className="mt-4 space-y-3">
                <Field label="Title"><input value={content.legal?.[k]?.title || ""} onChange={(e) => set(`legal.${k}.title`, e.target.value)} className={input} /></Field>
                <Field label="Subtitle"><input value={content.legal?.[k]?.subtitle || ""} onChange={(e) => set(`legal.${k}.subtitle`, e.target.value)} className={input} /></Field>
                <Field label="Body (line breaks preserved)"><textarea rows={10} value={content.legal?.[k]?.body || ""} onChange={(e) => set(`legal.${k}.body`, e.target.value)} className={`${input} resize-y font-mono text-xs`} /></Field>
              </div>
            </details>
          ))}
        </Card>

        {/* Marquee */}
        <Card title="Marquee strip" subtitle="The scrolling band below the hero">
          <ListEditor
            items={content.marquee}
            onChange={(items) => set("marquee", items)}
            renderRow={(item, set) => (
              <div className="grid grid-cols-[1fr_120px] gap-3 items-center">
                <input value={item.text} onChange={(e) => set("text", e.target.value)} className={input} placeholder="Text" />
                <label className="flex items-center gap-2 text-[12px] text-[#9a9787]">
                  <input type="checkbox" checked={!!item.highlight} onChange={(e) => set("highlight", e.target.checked)} />
                  Highlight
                </label>
              </div>
            )}
            empty={() => ({ text: "", highlight: false })}
          />
        </Card>

        {/* Pillars */}
        <Card title="Four pillars" subtitle="The black tenets section">
          <Field label="Eyebrow"><input value={content.pillars_eyebrow} onChange={(e) => set("pillars_eyebrow", e.target.value)} className={input} /></Field>
          <Field label="Section heading"><input value={content.pillars_heading} onChange={(e) => set("pillars_heading", e.target.value)} className={input} /></Field>
          <ListEditor
            items={content.pillars}
            onChange={(items) => set("pillars", items)}
            renderRow={(item, set) => (
              <div className="space-y-2">
                <div className="grid grid-cols-[80px_1fr] gap-3">
                  <input value={item.num} onChange={(e) => set("num", e.target.value)} className={input} placeholder="I" />
                  <input value={item.label} onChange={(e) => set("label", e.target.value)} className={input} placeholder="The Pulse" />
                </div>
                <input value={item.title} onChange={(e) => set("title", e.target.value)} className={input} placeholder="Title" />
                <textarea rows={2} value={item.body} onChange={(e) => set("body", e.target.value)} className={`${input} resize-none`} placeholder="Body" />
              </div>
            )}
            empty={() => ({ num: "", label: "", title: "", body: "" })}
          />
        </Card>

        {/* Categories */}
        <Card title="Categories split" subtitle="Shirts + Short Kurtas tiles">
          <Field label="Eyebrow"><input value={content.categories.eyebrow} onChange={(e) => set("categories.eyebrow", e.target.value)} className={input} /></Field>
          <Field label="Heading"><input value={content.categories.heading} onChange={(e) => set("categories.heading", e.target.value)} className={input} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Shirts image">
              <ImageUploader
                value={content.categories.shirts_image ? [content.categories.shirts_image] : []}
                onChange={(urls) => set("categories.shirts_image", urls[0] || "")}
                folder="asvasa/hero"
                max={1}
                testid="sections-categories-shirts"
                guidelines="1200 × 1500 px (4:5 portrait) · JPG/PNG · under 3 MB"
              />
            </Field>
            <Field label="Kurtas image">
              <ImageUploader
                value={content.categories.kurtas_image ? [content.categories.kurtas_image] : []}
                onChange={(urls) => set("categories.kurtas_image", urls[0] || "")}
                folder="asvasa/hero"
                max={1}
                testid="sections-categories-kurtas"
                guidelines="1200 × 1500 px (4:5 portrait) · JPG/PNG · under 3 MB"
              />
            </Field>
          </div>
        </Card>

        {/* Big CTA */}
        <Card title="Closing CTA" subtitle="Bottom black-block call-to-action">
          <Field label="Eyebrow"><input value={content.big_cta.eyebrow} onChange={(e) => set("big_cta.eyebrow", e.target.value)} className={input} /></Field>
          <Field label="Quote"><textarea rows={3} value={content.big_cta.quote} onChange={(e) => set("big_cta.quote", e.target.value)} className={`${input} resize-none`} data-testid="sections-cta-quote" /></Field>
          <Field label="Body"><textarea rows={3} value={content.big_cta.body} onChange={(e) => set("big_cta.body", e.target.value)} className={`${input} resize-none`} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Button label"><input value={content.big_cta.button_label} onChange={(e) => set("big_cta.button_label", e.target.value)} className={input} /></Field>
            <Field label="Button email"><input value={content.big_cta.button_email} onChange={(e) => set("big_cta.button_email", e.target.value)} className={input} /></Field>
          </div>
          <Field label="Email subject"><input value={content.big_cta.button_subject} onChange={(e) => set("big_cta.button_subject", e.target.value)} className={input} /></Field>
        </Card>

        {/* Save bar */}
        <div className="sticky bottom-6 bg-[#161617] border border-[#c3ba9e]/30 px-6 py-4 flex items-center justify-between shadow-2xl">
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#9a9787]">
            Changes go live the moment you save.
          </p>
          <div className="flex gap-3">
            <button onClick={onReset} className="border border-[#2a2a2d] text-[#9a9787] px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#1f1f21] flex items-center gap-2">
              <RotateCcw size={13} /> Reset
            </button>
            <button onClick={save} disabled={saving} className="bg-[#c3ba9e] text-[#0d0d0e] px-7 py-2.5 text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#FDFAF6] disabled:opacity-50 flex items-center gap-2" data-testid="sections-save">
              <Save size={13} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const input = "w-full bg-[#0d0d0e] border border-[#2a2a2d] focus:border-[#c3ba9e] outline-none px-3 py-2 text-[13px] text-[#e9e6db]";

function Card({ title, subtitle, children }) {
  return (
    <section className="bg-[#161617] border border-[#2a2a2d] p-7 space-y-4">
      <div>
        <h3 className="font-serif-display text-2xl text-[#c3ba9e]">{title}</h3>
        {subtitle && <p className="text-[11px] text-[#8c8a7e] mt-1 tracking-wider">{subtitle}</p>}
      </div>
      <div className="space-y-4 pt-2">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function ListEditor({ items, onChange, renderRow, empty }) {
  const update = (i, key, value) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it));
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, empty()]);
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="bg-[#0d0d0e] border border-[#2a2a2d] p-4 relative">
          {renderRow(item, (k, v) => update(i, k, v))}
          <button
            type="button"
            onClick={() => remove(i)}
            className="absolute top-2 right-2 p-1 text-[#5e5d54] hover:text-[#c97070]"
            aria-label="Remove"
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="w-full border border-dashed border-[#2a2a2d] py-3 text-[11px] tracking-[0.28em] uppercase text-[#8c8a7e] hover:text-[#c3ba9e] hover:border-[#c3ba9e]/40 flex items-center justify-center gap-2"
      >
        <Plus size={13} /> Add row
      </button>
    </div>
  );
}
